import { useEffect } from 'react';
import { z } from 'zod';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $createRangeSelection,
  $getNearestNodeFromDOMNode,
  $getNodeByKey,
  $insertNodes,
  $setSelection,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_HIGH,
  DRAGOVER_COMMAND,
  DRAGSTART_COMMAND,
  DROP_COMMAND,
} from 'lexical';
import {
  $createImageNode,
  $isImageNode,
  INSERT_IMAGE_COMMAND,
  ImageNode,
  type ImagePayload,
} from './ImageNode';

/**
 * Private drag type that marks a drag as an in-editor image *move* rather than
 * an external file drop. Kept out of the standard `text/*`/`Files` types so it
 * never collides with a real paste. Its presence on drop is what lets us move
 * the existing node instead of re-uploading the dragged image — the browser
 * (Chromium) synthesises a `File` for any dragged `<img>`, which otherwise
 * looks identical to a fresh file drop to `@lexical/rich-text` and triggers a
 * re-upload via DRAG_DROP_PASTE.
 */
const IMAGE_MOVE_DRAG_TYPE = 'application/x-lexical-image-move';

// Validated at the drop boundary rather than cast: the payload is read back
// from `dataTransfer`, so a malformed/foreign drag shouldn't reach the editor
// mutation with a half-shaped object.
const ImageMovePayloadSchema = z.object({
  key: z.string(),
  src: z.string(),
  documentId: z.string(),
  altText: z.string(),
  width: z.number().optional(),
});
type ImageMovePayload = z.infer<typeof ImageMovePayloadSchema>;

// Firefox exposes caretPositionFromPoint; Chromium/WebKit expose
// caretRangeFromPoint. Neither is in lib.dom for every target, so narrow here.
interface CaretPositionCapableDocument {
  caretRangeFromPoint?: (x: number, y: number) => Range | null;
  caretPositionFromPoint?: (
    x: number,
    y: number,
  ) => { offsetNode: Node; offset: number } | null;
}

/** Resolves the caret (as a DOM Range) at the drop point, cross-browser. */
function getDropRange(event: DragEvent): Range | null {
  const doc = document as Document & CaretPositionCapableDocument;
  if (typeof doc.caretRangeFromPoint === 'function') {
    return doc.caretRangeFromPoint(event.clientX, event.clientY);
  }
  if (typeof doc.caretPositionFromPoint === 'function') {
    const pos = doc.caretPositionFromPoint(event.clientX, event.clientY);
    if (!pos) return null;
    const range = document.createRange();
    range.setStart(pos.offsetNode, pos.offset);
    range.collapse(true);
    return range;
  }
  return null;
}

/**
 * A thin insertion line, drawn at the drop point while an image is dragged, so
 * there's a clear indication of where it will land. Rendered by us rather than
 * relying on the browser's native drop caret, which doesn't paint reliably
 * inside a Lexical contentEditable. Lazily created and appended to <body> so it
 * clears the editor's `overflow-hidden` wrapper; positioned with viewport
 * (`fixed`) coordinates that match the DragEvent's client coords.
 */
function createDropCaretController() {
  let el: HTMLDivElement | null = null;

  const ensure = (): HTMLDivElement => {
    if (!el) {
      el = document.createElement('div');
      el.style.position = 'fixed';
      el.style.width = '2px';
      el.style.pointerEvents = 'none';
      el.style.background = 'var(--ring, #2563eb)';
      el.style.borderRadius = '1px';
      el.style.zIndex = '2147483647';
    }
    return el;
  };

  return {
    show(event: DragEvent) {
      const caret = ensure();
      const rect = getDropRange(event)?.getBoundingClientRect();
      if (rect && (rect.height > 0 || rect.top !== 0 || rect.left !== 0)) {
        caret.style.left = `${rect.left}px`;
        caret.style.top = `${rect.top}px`;
        caret.style.height = `${rect.height || 18}px`;
      } else {
        // No usable caret rect (e.g. dropping onto an empty line) — fall back
        // to the pointer position with an estimated line height.
        caret.style.left = `${event.clientX}px`;
        caret.style.top = `${event.clientY - 9}px`;
        caret.style.height = '18px';
      }
      if (!caret.isConnected) document.body.appendChild(caret);
    },
    hide() {
      if (el?.isConnected) el.remove();
    },
  };
}

/**
 * Wires INSERT_IMAGE_COMMAND to insert an ImageNode at the current selection,
 * and handles dragging an existing image to a new position within the editor.
 *
 * The move is intercepted at COMMAND_PRIORITY_HIGH so it runs *before*
 * `@lexical/rich-text`'s DROP handler (COMMAND_PRIORITY_EDITOR): we tag the
 * drag with {@link IMAGE_MOVE_DRAG_TYPE} on dragstart, then on drop move the
 * node (remove + reinsert with the same payload) and return `true`. Returning
 * `true` stops rich-text's DROP handler, so it never dispatches
 * DRAG_DROP_PASTE and the image is moved rather than re-uploaded. While the
 * drag is in flight we paint an insertion line at the drop point and let the
 * browser show the image's own drag ghost.
 *
 * Safe to render unconditionally — it only acts on our own image nodes.
 */
export function ImagePlugin(): null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!editor.hasNodes([ImageNode])) {
      throw new Error('ImagePlugin: ImageNode not registered on editor');
    }

    const dropCaret = createDropCaretController();

    const unregisterInsert = editor.registerCommand<ImagePayload>(
      INSERT_IMAGE_COMMAND,
      (payload) => {
        editor.update(() => {
          $insertNodes([$createImageNode(payload)]);
        });
        return true;
      },
      COMMAND_PRIORITY_EDITOR,
    );

    const unregisterDragStart = editor.registerCommand<DragEvent>(
      DRAGSTART_COMMAND,
      (event) => {
        const target = event.target;
        const dataTransfer = event.dataTransfer;
        if (!(target instanceof Node) || !dataTransfer) return false;
        const node = $getNearestNodeFromDOMNode(target);
        if (!$isImageNode(node)) return false;
        const payload: ImageMovePayload = {
          key: node.getKey(),
          src: node.getSrc(),
          documentId: node.getDocumentId(),
          altText: node.getAltText(),
          width: node.getWidth(),
        };
        dataTransfer.setData(IMAGE_MOVE_DRAG_TYPE, JSON.stringify(payload));
        // Move, not copy — yields the plain move cursor rather than the
        // copy/plus-sign one. The browser shows the dragged <img> as the ghost.
        dataTransfer.effectAllowed = 'move';
        return true;
      },
      COMMAND_PRIORITY_HIGH,
    );

    const unregisterDragOver = editor.registerCommand<DragEvent>(
      DRAGOVER_COMMAND,
      (event) => {
        if (!event.dataTransfer?.types.includes(IMAGE_MOVE_DRAG_TYPE)) {
          return false;
        }
        // A dragover without preventDefault cancels the drop; dropEffect
        // 'move' keeps the move cursor (not the copy/plus-sign) over the drop.
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        dropCaret.show(event);
        return true;
      },
      COMMAND_PRIORITY_HIGH,
    );

    const unregisterDrop = editor.registerCommand<DragEvent>(
      DROP_COMMAND,
      (event) => {
        const raw = event.dataTransfer?.getData(IMAGE_MOVE_DRAG_TYPE);
        if (!raw) return false;
        let parsedJson: unknown;
        try {
          parsedJson = JSON.parse(raw);
        } catch {
          return false;
        }
        const parsed = ImageMovePayloadSchema.safeParse(parsedJson);
        if (!parsed.success) return false;
        const payload = parsed.data;
        event.preventDefault();
        dropCaret.hide();
        const range = getDropRange(event);
        const moved = $getNodeByKey(payload.key);
        if ($isImageNode(moved)) moved.remove();
        const selection = $createRangeSelection();
        if (range) selection.applyDOMRange(range);
        $setSelection(selection);
        $insertNodes([
          $createImageNode({
            src: payload.src,
            documentId: payload.documentId,
            altText: payload.altText,
            width: payload.width,
          }),
        ]);
        return true;
      },
      COMMAND_PRIORITY_HIGH,
    );

    // Clear the insertion line if the drag ends anywhere (dropped outside the
    // editor, or cancelled with Esc) — those never reach our DROP handler.
    const onDragEnd = () => dropCaret.hide();
    window.addEventListener('dragend', onDragEnd);
    window.addEventListener('drop', onDragEnd);

    return () => {
      unregisterInsert();
      unregisterDragStart();
      unregisterDragOver();
      unregisterDrop();
      window.removeEventListener('dragend', onDragEnd);
      window.removeEventListener('drop', onDragEnd);
      dropCaret.hide();
    };
  }, [editor]);

  return null;
}
